import React, { useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, IconButton, useTheme } from '@mui/material';
import { Close as CloseIcon, Map as MapIcon } from '@mui/icons-material';
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF } from '@react-google-maps/api';

// One marker per BUILDING (not per flat) — every flat in a building shares one physical
// location, so per-flat markers would just overlap identically. Computed client-side by
// PropertiesPage from the properties it already holds in state; see its `mapMarkers` useMemo.
export interface PropertyMapMarker {
  normalized_address: string;
  latitude: number;
  longitude: number;
  formatted_address: string | null;
  flat_count: number;
  owner_name: string | null;
}

// Same Google Maps Platform project/key as the address finder (routes/accounting.py's
// GOOGLE_MAPS_API_KEY, server-side) — this one is the browser-side Maps JavaScript API key
// instead, safe to ship in the bundle since it's restricted by HTTP referrer in GCP rather than
// kept secret. Falls back to rendering the "no verified addresses" empty state's styling if
// unset, rather than a broken/blank map.
const GOOGLE_MAPS_JS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const mapContainerStyle = { height: '100%', width: '100%' };

const PropertiesMapDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  markers: PropertyMapMarker[];
}> = ({ open, onClose, markers }) => {
  const { palette } = useTheme();
  const [activeMarker, setActiveMarker] = useState<string | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_JS_API_KEY,
  });

  // Center/zoom only need computing once per marker set (not on every render) — a single
  // building centers directly on it; multiple buildings average their coordinates and let
  // onLoad's fitBounds below handle the actual framing.
  const center = useMemo(() => {
    if (markers.length === 0) return { lat: 51.5072, lng: -0.1276 }; // London — never actually shown (empty state renders instead)
    const avgLat = markers.reduce((s, m) => s + m.latitude, 0) / markers.length;
    const avgLng = markers.reduce((s, m) => s + m.longitude, 0) / markers.length;
    return { lat: avgLat, lng: avgLng };
  }, [markers]);

  const onMapLoad = (map: google.maps.Map) => {
    if (markers.length <= 1) return;
    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach(m => bounds.extend({ lat: m.latitude, lng: m.longitude }));
    map.fitBounds(bounds, 40);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 700 }}>
        Properties map
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {markers.length === 0 ? (
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
            py: 8, px: 4, textAlign: 'center',
          }}>
            <MapIcon sx={{ fontSize: 40, color: palette.tokens.hairline }} />
            <Typography variant="titleSm" sx={{ color: palette.tokens.muted }}>
              No verified addresses yet
            </Typography>
            <Typography variant="bodySm" sx={{ color: palette.tokens.mutedSoft, maxWidth: 360 }}>
              Pick a suggestion from the address finder when adding or editing a property to see
              it here — a property you've only typed in free text won't show on the map.
            </Typography>
          </Box>
        ) : loadError ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
            <Typography sx={{ color: palette.tokens.error }}>Couldn't load Google Maps.</Typography>
          </Box>
        ) : !isLoaded ? (
          <Box sx={{ height: 520 }} />
        ) : (
          <Box sx={{ height: 520 }}>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={markers.length === 1 ? 14 : 11}
              onLoad={onMapLoad}
              options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
            >
              {markers.map(marker => (
                <MarkerF
                  key={marker.normalized_address}
                  position={{ lat: marker.latitude, lng: marker.longitude }}
                  onClick={() => setActiveMarker(marker.normalized_address)}
                >
                  {activeMarker === marker.normalized_address && (
                    <InfoWindowF onCloseClick={() => setActiveMarker(null)}>
                      <Box sx={{ minWidth: 160 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                          {marker.normalized_address}
                        </Typography>
                        {marker.formatted_address && (
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {marker.formatted_address}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                          {marker.flat_count} {marker.flat_count === 1 ? 'flat' : 'flats'}
                          {marker.owner_name ? ` · ${marker.owner_name}` : ''}
                        </Typography>
                      </Box>
                    </InfoWindowF>
                  )}
                </MarkerF>
              ))}
            </GoogleMap>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: palette.tokens.muted }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropertiesMapDialog;
